// ── AssistantShell ────────────────────────────────────────────────────────────
// Shared visual chrome for all contextual assistant panels.
// Handles: header + collapse strip, pinned pills grid (2-col cyan),
//          chip-above-input flow, standard footer (input + send).
// Parents supply: message content via children + send callback.

import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { WithTooltip } from './Tooltip.js';
import { Icon } from '../Icon.js';
import { ClaudeAssistantPanel } from './ClaudeAssistantPanel.js';
import { useSpeechToText } from '../../hooks/useSpeechToText.js';
import { useSecretStatus } from '../../hooks/useSettings.js';
import { useUiFlag } from '../../hooks/useUiPrefs.js';

let voiceKeyframesInjected = false;
function injectVoiceKeyframes() {
  if (voiceKeyframesInjected || typeof document === 'undefined') return;
  voiceKeyframesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes evo-mic-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.45; }
    }
  `;
  document.head.appendChild(style);
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AssistantPillDef {
  toolName: string;
  label: string;
  requiresInput: boolean;
}

export type AssistantTab = 'chat' | 'tools';

interface Props {
  title: string;
  /** Pinned pills shown above message area. Click → chip-above-input flow for requiresInput. */
  pills?: AssistantPillDef[];
  onSendMessage: (message: string) => void;
  isStreaming?: boolean;
  inputPlaceholder?: string;
  /** Placeholder shown in footer input when a requiresInput pill is active. */
  chipPlaceholder?: string;
  inputDisabled?: boolean;
  /** localStorage key for collapse persistence — unique per module. Default: 'evo-assistant-collapsed'. */
  storageKey?: string;
  /** Custom footer (textarea panels, etc). Skips default input+send footer. */
  footerOverride?: ReactNode;
  /** Content pinned below header, above pills section (e.g. FollowupAnalysisPanel). */
  topContent?: ReactNode;
  /**
   * Herramientas del módulo, en su propia pestaña.
   *
   * Distinto de `topContent`, que se apila ARRIBA de la conversación y comparte
   * la columna con ella. Cuando el módulo trae herramientas de verdad —el editor
   * de páginas— apilarlas deja las dos cosas amontonadas y ninguna cómoda. Con
   * esto el panel muestra una u otra, nunca las dos.
   *
   * Sin este prop no aparece ninguna pestaña: los módulos que solo tienen chat
   * siguen viéndose igual que antes.
   */
  toolsContent?: ReactNode;
  /** Pestaña activa. Sin estos dos, el shell la maneja solo. */
  activeTab?: AssistantTab;
  onActiveTabChange?: (tab: AssistantTab) => void;
  /** TTS (Fase 4) — provisto por useAssistant. Sin estos props, los controles de voz de salida no se muestran. */
  muted?: boolean;
  onToggleMute?: () => void;
  isSpeaking?: boolean;
  onStopSpeaking?: () => void;
  /** Circuit breaker: se cortó de pedir audio tras una falla real — se muestra distinto de "muteado a propósito". */
  ttsBroken?: boolean;
  ttsErrorMessage?: string | null;
  onReactivateTts?: () => void;
  /** Visible cuando la última respuesta no trajo línea hablada usable — nunca silencio sin explicación. */
  voiceNote?: string | null;
  /** Historial (2026-08-06) — sin esto, el botón no se muestra (mismo criterio que muted/onToggleMute). */
  onOpenHistory?: () => void;
  /** Memoria entre sesiones (2026-08-06) — mismo criterio que onOpenHistory. */
  onOpenMemory?: () => void;
  children: ReactNode;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AssistantShell({
  title,
  pills,
  onSendMessage,
  isStreaming = false,
  inputPlaceholder,
  chipPlaceholder,
  inputDisabled = false,
  storageKey,
  footerOverride,
  topContent,
  toolsContent,
  activeTab,
  onActiveTabChange,
  muted,
  onToggleMute,
  isSpeaking = false,
  onStopSpeaking,
  ttsBroken = false,
  ttsErrorMessage,
  onReactivateTts,
  voiceNote,
  onOpenHistory,
  onOpenMemory,
  children,
}: Props) {
  const { t } = useTranslation();
  injectVoiceKeyframes();

  // La pestaña puede venir de afuera (Proyectos la cambia al entrar en Editar) o
  // manejarse acá. Sin toolsContent no hay pestañas y esto no se usa.
  const [tabPropia, setTabPropia] = useState<AssistantTab>('chat');
  const tabActiva = activeTab ?? tabPropia;
  const cambiarTab = useCallback((t: AssistantTab) => {
    setTabPropia(t);
    onActiveTabChange?.(t);
  }, [onActiveTabChange]);

  const [input, setInput]           = useState('');
  const [activePill, setActivePill] = useState<AssistantPillDef | null>(null);
  const inputRef                    = useRef<HTMLTextAreaElement>(null);

  const isDisabled = isStreaming || inputDisabled;

  // ── Voz de entrada (Fase 4) — push-to-talk ──────────────────────────────────
  const { data: groqStatus } = useSecretStatus('groq-api-key');
  const micEnabled = groqStatus?.configured === true;

  const handleTranscribed = useCallback((text: string) => {
    setInput(text);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
      }
    });
  }, []);
  const stt = useSpeechToText(handleTranscribed);

  // Atajo de teclado: Ctrl+Espacio sostenido, mismo gesto que el botón.
  // !e.shiftKey es real, no cosmético — Ctrl+Shift+Espacio es el atajo del
  // modo de conversación por voz (useVoiceConversationMode.ts), un gesto
  // DISTINTO. Sin este chequeo, ambos atajos matcheaban al mismo tiempo:
  // abría el modo de voz Y arrancaba push-to-talk en simultáneo, los dos
  // pidiendo el mic — al soltar Espacio (keyup no mira ctrlKey tampoco),
  // push-to-talk paraba y volcaba la transcripción al input del dock en vez
  // de mandarla sola. Bug real, reportado por Eduardo en producción
  // (2026-08-06) — el modo de voz "no hacía nada" porque en realidad estaba
  // corriendo push-to-talk por debajo.
  const spaceHeldRef = useRef(false);
  useEffect(() => {
    if (!micEnabled || isDisabled) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey && !e.shiftKey && e.code === 'Space' && !spaceHeldRef.current) {
        e.preventDefault();
        spaceHeldRef.current = true;
        stt.startRecording();
      }
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.code === 'Space' && spaceHeldRef.current) {
        e.preventDefault();
        spaceHeldRef.current = false;
        stt.stopRecording();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [micEnabled, isDisabled, stt.startRecording, stt.stopRecording]);

  // El colapso vive en el servidor, por usuario, para que viaje entre sesiones y
  // entre máquinas. `useUiFlag` conserva un eco en localStorage solo para el
  // primer pintado: sin él, el panel se dibuja abierto y se cierra medio segundo
  // después, y ese parpadeo se ve peor que no recordar nada.
  //
  // `storageKey` sigue en la firma para los montajes que quieran su propia
  // clave, pero el estado compartido es el del panel de Evo Brain.
  const [collapsed, setCollapsed] = useUiFlag('assistant.collapsed');

  function toggleCollapse() {
    setCollapsed(!collapsed);
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  function handlePillClick(pill: AssistantPillDef) {
    if (isStreaming) return;
    if (pill.requiresInput) {
      setActivePill(pill);
      setInput('');
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      onSendMessage(pill.label);
    }
  }

  function handleChipCancel() {
    setActivePill(null);
    setInput('');
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function handleInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  }

  function handleSend() {
    const trimmed = input.trim();
    if (!trimmed || isDisabled) return;
    const msg = activePill ? `${activePill.label}: ${trimmed}` : trimmed;
    setInput('');
    setActivePill(null);
    onSendMessage(msg);
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  }

  // ── Collapsed strip ───────────────────────────────────────────────────────

  const collapsedStrip = (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: 'var(--evo-space-4)',
      paddingBottom: 'var(--evo-space-3)',
      background: 'var(--evo-bg-3)',
    }}>
      {/* Sparkle — click to expand */}
      <button
        onClick={toggleCollapse}
        aria-label={t('assistant.expandLabel')}
        title={t('assistant.expandLabel')}
        style={{
          width: '36px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--evo-cyan)',
          borderRadius: 'var(--evo-r-sm)',
          transition: 'opacity 120ms ease-out',
        }}
      >
        <Icon name="sparkle" size={20} />
      </button>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Chevron left — decorative, sparkle button above covers the action */}
      <button
        onClick={toggleCollapse}
        aria-hidden="true"
        tabIndex={-1}
        style={{
          width: '28px',
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--evo-fg-3)',
          borderRadius: 'var(--evo-r-sm)',
          transition: 'color 120ms ease-out',
        }}
      >
        <Icon name="chevron-left" size={16} />
      </button>
    </div>
  );

  // ── Pinned pills section ──────────────────────────────────────────────────
  // Hidden when a pill chip is active (user is typing the instruction).

  const hasPills = pills && pills.length > 0;

  // Botones predeterminados RETIRADOS de todos los módulos (2026-08-30):
  // el panel arranca limpio; las acciones viven en cada pantalla.
  void hasPills;
  const pillsSection = false && activePill == null ? (
    <div style={{
      padding: 'var(--evo-space-2) var(--evo-space-3)',
      borderBottom: '1px solid var(--evo-stroke)',
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--evo-space-2)',
      }}>
        {pills!.map((pill, i) => {
          // Último pill huérfano (total impar) ocupa el ancho completo en vez
          // de quedar a medias en un grid de 2 columnas fijas.
          const isOrphan = i === pills!.length - 1 && pills!.length % 2 === 1;
          return (
            <button
              key={pill.toolName}
              disabled={isStreaming}
              onClick={() => handlePillClick(pill)}
              style={{
                textAlign: 'left',
                padding: '7px 10px',
                borderRadius: 'var(--evo-r)',
                border: '1px solid var(--evo-cyan)',
                background: 'transparent',
                color: isStreaming ? 'var(--evo-fg-4)' : 'var(--evo-cyan)',
                fontFamily: 'var(--evo-font)',
                fontSize: 'var(--evo-fs-caption)',
                cursor: isStreaming ? 'not-allowed' : 'pointer',
                lineHeight: 1.3,
                opacity: isStreaming ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'opacity 150ms ease-out',
                gridColumn: isOrphan ? '1 / -1' : undefined,
              }}
            >
              {pill.requiresInput && <Icon name="pencil" size={10} />}
              {t(`assistant.pills.${pill.toolName}`, { defaultValue: pill.label })}
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

  const enHerramientas = toolsContent != null && tabActiva === 'tools';

  const pinnedTop = (topContent != null || pillsSection != null) ? (
    <>
      {topContent}
      {pillsSection}
    </>
  ) : null;

  // ── Historial (2026-08-06) — mismo estilo que collapseBtn, un botón más en la fila ─
  const historyBtn = onOpenHistory != null ? (
    <WithTooltip label={t('assistant.tooltip.history')}>
    <button
      onClick={onOpenHistory}
      aria-label={t('assistant.tooltip.history')}
      title={t('assistant.tooltip.history')}
      style={{
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--evo-fg-3)',
        borderRadius: 'var(--evo-r-sm)',
        transition: 'color 120ms ease-out',
        flexShrink: 0,
      }}
    >
      <Icon name="activity" size={15} />
    </button>
    </WithTooltip>
  ) : null;

  // ── Memoria (2026-08-06) — mismo estilo que historyBtn, un botón más en la fila ─
  const memoryBtn = onOpenMemory != null ? (
    <WithTooltip label={t('assistant.tooltip.memory')}>
    <button
      onClick={onOpenMemory}
      aria-label={t('assistant.tooltip.memory')}
      title={t('assistant.tooltip.memory')}
      style={{
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--evo-fg-3)',
        borderRadius: 'var(--evo-r-sm)',
        transition: 'color 120ms ease-out',
        flexShrink: 0,
      }}
    >
      <Icon name="bookmark" size={15} />
    </button>
    </WithTooltip>
  ) : null;

  // ── Collapse button for header ────────────────────────────────────────────

  const collapseBtn = (
    <WithTooltip label={t('assistant.collapseLabel')}>
    <button
      onClick={toggleCollapse}
      aria-label={t('assistant.collapseLabel')}
      title={t('assistant.collapseLabel')}
      style={{
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--evo-fg-3)',
        borderRadius: 'var(--evo-r-sm)',
        transition: 'color 120ms ease-out',
        flexShrink: 0,
      }}
    >
      <Icon name="chevron-right" size={16} />
    </button>
    </WithTooltip>
  );

  // ── Voz de salida — mute persistente + cortar lectura (Fase 4) ─────────────
  // Solo se muestran si el panel llamador pasó estos props (vienen de
  // useAssistant) — sin ellos, TTS no está wireado para ese panel.

  // Roto (falló de verdad, se cortó de pedir audio) es un estado DISTINTO de
  // muteado a propósito — mismo botón, pero rojo, y el click reactiva en vez
  // de mutear/desmutear. Ver tripTtsBreaker/reactivateTts en useAssistant.
  const muteBtnLabel = ttsBroken
    ? t('assistant.tts.reactivate')
    : (muted ? t('assistant.tooltip.readAloud') : t('assistant.tts.mute'));

  const muteBtn = onToggleMute != null ? (
    <WithTooltip label={muteBtnLabel}>
    <button
      onClick={ttsBroken ? onReactivateTts : onToggleMute}
      aria-label={muteBtnLabel}
      title={muteBtnLabel}
      style={{
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: ttsBroken ? 'var(--evo-danger)' : (muted ? 'var(--evo-fg-4)' : 'var(--evo-fg-3)'),
        borderRadius: 'var(--evo-r-sm)',
        transition: 'color 120ms ease-out',
        flexShrink: 0,
      }}
    >
      <Icon name={(ttsBroken || muted) ? 'volume-mute' : 'volume'} size={15} />
    </button>
    </WithTooltip>
  ) : null;

  const stopSpeakingBtn = (isSpeaking && onStopSpeaking != null) ? (
    <WithTooltip label={t('assistant.tts.stop')}>
    <button
      onClick={onStopSpeaking}
      aria-label={t('assistant.tts.stop')}
      title={t('assistant.tts.stop')}
      style={{
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--evo-cyan)',
        borderRadius: 'var(--evo-r-sm)',
        flexShrink: 0,
      }}
    >
      <Icon name="stop" size={13} />
    </button>
    </WithTooltip>
  ) : null;

  // ── Pestañas: conversación / herramientas ─────────────────────────────────
  // Van en la fila del header, con el resto de los botones de icono: es donde ya
  // se mira para cambiar algo del panel.
  const tabBtn = (tab: AssistantTab, icono: 'messages' | 'pencil', etiqueta: string) => {
    const activa = tabActiva === tab;
    return (
      <WithTooltip label={etiqueta}>
      <button
        onClick={() => { cambiarTab(tab); }}
        aria-label={etiqueta}
        aria-pressed={activa}
        title={etiqueta}
        style={{
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: activa ? 'var(--evo-bg-3)' : 'none',
          border: 'none',
          cursor: 'pointer',
          color: activa ? 'var(--evo-cyan)' : 'var(--evo-fg-3)',
          borderRadius: 'var(--evo-r-sm)',
          transition: 'color 120ms ease-out, background 120ms ease-out',
          flexShrink: 0,
        }}
      >
        <Icon name={icono} size={15} />
      </button>
      </WithTooltip>
    );
  };

  // Las pestañas conversación/herramientas SE QUEDAN: herramientas es el
  // panel del editor (comentarios, aplicar, inspector, formato, ancestros).
  // Lo retirado del rediseño V2 son las pills predeterminadas, no esto.
  const tabs = toolsContent != null ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginRight: 'var(--evo-space-1)' }}>
      {tabBtn('chat', 'messages', t('assistant.tabs.chat'))}
      {tabBtn('tools', 'pencil', t('assistant.tabs.tools'))}
    </div>
  ) : null;

  const headerControls = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-1)' }}>
      {tabs}
      {stopSpeakingBtn}
      {historyBtn}
      {memoryBtn}
      {muteBtn}
      {collapseBtn}
    </div>
  );

  // ── Standard footer — input + send + chip ─────────────────────────────────

  const standardFooter = (
    <div>
      {/* Chip — visible when a requiresInput pill is active */}
      {activePill != null && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--evo-space-2)',
          marginBottom: 'var(--evo-space-2)',
          padding: '5px 8px',
          background: 'var(--evo-bg-2)',
          border: '1px solid var(--evo-cyan)',
          borderRadius: 'var(--evo-r-sm)',
        }}>
          <span style={{ color: 'var(--evo-cyan)', flexShrink: 0, display: 'inline-flex' }}>
            <Icon name="arrow-right" size={10} />
          </span>
          <span style={{
            flex: 1,
            fontSize: 'var(--evo-fs-caption)',
            fontFamily: 'var(--evo-font)',
            color: 'var(--evo-cyan)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {t(`assistant.pills.${activePill.toolName}`, { defaultValue: activePill.label })}
          </span>
          <button
            onClick={handleChipCancel}
            title={t('common.remove')}
            aria-label={t('common.remove')}
            style={{
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--evo-fg-3)',
              padding: 0,
              borderRadius: 'var(--evo-r-sm)',
            }}
          >
            <Icon name="close" size={10} />
          </button>
        </div>
      )}

      {/* Input row */}
      <div style={{ display: 'flex', gap: 'var(--evo-space-2)', alignItems: 'flex-end' }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
            if (e.key === 'Escape' && activePill != null) { e.preventDefault(); handleChipCancel(); }
          }}
          disabled={isDisabled}
          rows={1}
          aria-label={
            activePill != null
              ? (chipPlaceholder ?? t('assistant.dock.chipPlaceholderDefault'))
              : (inputPlaceholder ?? t('assistant.dock.inputPlaceholder'))
          }
          placeholder={
            activePill != null
              ? (chipPlaceholder ?? t('assistant.dock.chipPlaceholderDefault'))
              : (inputPlaceholder ?? t('assistant.dock.inputPlaceholder'))
          }
          style={{
            flex: 1,
            minHeight: '32px',
            maxHeight: '200px',
            padding: '7px 10px',
            background: 'var(--evo-bg)',
            border: `1px solid ${activePill != null ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`,
            borderRadius: 'var(--evo-r)',
            color: 'var(--evo-fg-1)',
            fontFamily: 'var(--evo-font)',
            fontSize: 'var(--evo-fs-label)',
            opacity: isDisabled ? 0.55 : 1,
            outline: 'none',
            resize: 'none',
            overflowY: 'auto',
            lineHeight: '1.45',
            transition: 'border-color 120ms ease-out',
          }}
        />
        {micEnabled && (
          <WithTooltip label={stt.isRecording ? t('assistant.mic.recording') : t('assistant.tooltip.dictate')}>
          <button
            disabled={isDisabled || stt.isTranscribing}
            onMouseDown={stt.startRecording}
            onMouseUp={stt.stopRecording}
            onMouseLeave={() => { if (stt.isRecording) stt.stopRecording(); }}
            onTouchStart={e => { e.preventDefault(); stt.startRecording(); }}
            onTouchEnd={e => { e.preventDefault(); stt.stopRecording(); }}
            aria-label={stt.isRecording ? t('assistant.mic.recording') : t('assistant.tooltip.dictate')}
            title={stt.isRecording ? t('assistant.mic.recording') : t('assistant.tooltip.dictate')}
            style={{
              width: '32px',
              height: '32px',
              // Círculo relleno en cian con el icono en negro: dictar es una
              // acción que se busca, y en gris sobre transparente se perdía
              // entre los demás iconos de la fila.
              borderRadius: 'var(--evo-r-pill)',
              border: `1px solid ${stt.isRecording ? 'var(--evo-danger)' : 'var(--evo-cyan)'}`,
              background: stt.isRecording ? 'var(--evo-danger)' : 'var(--evo-cyan)',
              color: stt.isRecording ? '#fff' : 'var(--evo-fg-on-cyan)',
              cursor: (isDisabled || stt.isTranscribing) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              opacity: (isDisabled || stt.isTranscribing) ? 0.5 : 1,
              animation: stt.isRecording ? 'evo-mic-pulse 1s ease-in-out infinite' : 'none',
              transition: 'background 120ms ease-out, border-color 120ms ease-out',
            }}
          >
            <Icon name="mic" size={14} />
          </button>
          </WithTooltip>
        )}
        <WithTooltip label={t('assistant.tooltip.send')}>
        <button
          disabled={isDisabled || !input.trim()}
          onClick={handleSend}
          title={t('assistant.tooltip.send')}
          aria-label={t('assistant.tooltip.send')}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: 'var(--evo-r)',
            border: 'none',
            background: (!isDisabled && input.trim()) ? 'var(--evo-cyan)' : 'var(--evo-stroke)',
            color: (!isDisabled && input.trim()) ? 'var(--evo-fg-on-accent)' : 'var(--evo-fg-3)',
            cursor: (!isDisabled && input.trim()) ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'background 120ms ease-out',
          }}
        >
          <Icon name="send" size={14} />
        </button>
        </WithTooltip>
      </div>

      {micEnabled && (stt.isRecording || stt.isTranscribing) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '5px', marginTop: 'var(--evo-space-1)',
          fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)',
        }}>
          {stt.isRecording ? t('assistant.mic.recording') : t('assistant.mic.transcribing')}
        </div>
      )}

      {stt.error != null && (
        <div style={{
          marginTop: 'var(--evo-space-1)', fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)',
          color: 'var(--evo-danger)',
        }}>
          {t('assistant.mic.errorLabel')}: {stt.error}
        </div>
      )}

      {ttsBroken && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '5px', marginTop: 'var(--evo-space-1)',
          fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-danger)',
        }}>
          {t('assistant.tts.brokenLabel')}{ttsErrorMessage ? `: ${ttsErrorMessage}` : ''} — {t('assistant.tts.brokenHint')}
        </div>
      )}

      {!ttsBroken && voiceNote != null && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '5px', marginTop: 'var(--evo-space-1)',
          fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)',
        }}>
          {voiceNote}
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      width: collapsed ? 'var(--evo-assistant-w-collapsed)' : 'var(--evo-claude-panel-w)',
      flexShrink: 0,
      borderLeft: '1px solid var(--evo-stroke)',
      height: '100%',
      overflow: 'hidden',
      transition: 'width var(--evo-motion-collapse)',
      // El panel mide 320px y muestra texto que nadie controla: URLs, nombres de
      // tool, ids de elemento, respuestas del modelo. Una palabra sin espacios
      // más ancha que el panel se salía del borde — medido en producción, tres
      // zonas distintas (el título, una fila de resultado de tool y una burbuja).
      //
      // Va acá y no en cada caller porque el problema es del contenedor, no del
      // contenido: cualquier módulo que monte algo en el dock hereda el corte.
      // 'anywhere' y no 'break-word' porque además achica el ancho mínimo del
      // elemento, que es lo que permite que un hijo flex se encoja en vez de
      // empujar. Las palabras normales no se parten: solo corta la que no entra.
      overflowWrap: 'anywhere',
    }}>
      {collapsed ? collapsedStrip : (
        enHerramientas ? (
          // En herramientas no se muestra ni la conversación ni el campo de
          // escribir: son dos trabajos distintos y mezclarlos fue el problema.
          <ClaudeAssistantPanel title={title} headerRight={headerControls}>
            {toolsContent}
          </ClaudeAssistantPanel>
        ) : (
          <ClaudeAssistantPanel
            title={title}
            headerRight={headerControls}
            pinnedTop={pinnedTop}
            footer={footerOverride ?? standardFooter}
          >
            {children}
          </ClaudeAssistantPanel>
        )
      )}
    </div>
  );
}
